import { useState } from 'react';
import { 
  FileText, 
  Server, 
  Settings, 
  Terminal, 
  AlertTriangle, 
  ExternalLink,
  Copy,
  Check,
  Network,
  FileKey,
  Wrench,
  CheckCircle2,
  Database,
  Code,
  Layers,
  Cloud,
  Package,
  Search,
  ChevronRight,
  ChevronDown,
  Briefcase,
  TrendingUp,
  Rocket,
  Shield,
  Zap,
  Globe,
  Smartphone,
  Clock,
  Target,
  Users,
  Wifi,
  WifiOff,
  Printer,
  Receipt,
  CalendarDays,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';

import { DATABASE_DOMAINS, DATABASE_TABLES, getTablesByDomain, type TableSchema } from '@/data/database-schema';
import { HOOK_CATEGORIES, HOOKS_DOCUMENTATION, getHooksByCategory } from '@/data/hooks-documentation';
import { COMPONENT_MODULES, getTotalComponentsCount } from '@/data/components-documentation';
import { EDGE_FUNCTION_CATEGORIES, EDGE_FUNCTIONS, getEdgeFunctionsByCategory } from '@/data/edge-functions-documentation';
import { LIB_CATEGORIES, LIBS_DOCUMENTATION, getLibsByCategory, getTotalFunctionsCount } from '@/data/libs-documentation';

export default function AdminDocumentacaoPage() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTable, setSelectedTable] = useState<TableSchema | null>(null);
  const [expandedDomains, setExpandedDomains] = useState<string[]>(['caixa']);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const CopyButton = ({ text, id }: { text: string; id: string }) => (
    <Button
      variant="ghost"
      size="icon"
      className="absolute right-2 top-2 h-8 w-8"
      onClick={() => copyToClipboard(text, id)}
    >
      {copiedCode === id ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </Button>
  );

  const CodeBlock = ({ code, id, language = 'bash' }: { code: string; id: string; language?: string }) => (
    <div className="relative">
      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
        <code>{code}</code>
      </pre>
      <CopyButton text={code} id={id} />
    </div>
  );

  const toggleDomain = (domainId: string) => {
    setExpandedDomains(prev => 
      prev.includes(domainId) 
        ? prev.filter(d => d !== domainId)
        : [...prev, domainId]
    );
  };

  const filteredTables = DATABASE_TABLES.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold flex items-center gap-2">
          <FileText className="h-8 w-8 text-primary" />
          Documentação Técnica
        </h1>
        <p className="text-muted-foreground mt-1">
          Manual completo do sistema Altus Food - Banco de dados, Hooks, Componentes e APIs
        </p>
      </div>

      <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
        <AlertTriangle className="h-5 w-5 text-amber-500" />
        <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
          Informações confidenciais - Acesso restrito a Super Admins
        </span>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{DATABASE_TABLES.length}</div>
            <div className="text-xs text-muted-foreground">Tabelas</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{HOOKS_DOCUMENTATION.length}</div>
            <div className="text-xs text-muted-foreground">Hooks</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{getTotalComponentsCount()}</div>
            <div className="text-xs text-muted-foreground">Componentes</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{EDGE_FUNCTIONS.length}</div>
            <div className="text-xs text-muted-foreground">Edge Functions</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{getTotalFunctionsCount()}</div>
            <div className="text-xs text-muted-foreground">Lib Functions</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="database" className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="database" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Banco de Dados
          </TabsTrigger>
          <TabsTrigger value="hooks" className="flex items-center gap-2">
            <Code className="h-4 w-4" />
            Hooks
          </TabsTrigger>
          <TabsTrigger value="componentes" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Componentes
          </TabsTrigger>
          <TabsTrigger value="edge-functions" className="flex items-center gap-2">
            <Cloud className="h-4 w-4" />
            Edge Functions
          </TabsTrigger>
          <TabsTrigger value="libs" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Libs
          </TabsTrigger>
          <TabsTrigger value="arquitetura" className="flex items-center gap-2">
            <Network className="h-4 w-4" />
            Arquitetura
          </TabsTrigger>
          <TabsTrigger value="investidores" className="flex items-center gap-2 bg-primary/10 text-primary">
            <Briefcase className="h-4 w-4" />
            Para Investidores
          </TabsTrigger>
        </TabsList>

        {/* Tab: Banco de Dados */}
        <TabsContent value="database" className="space-y-6">
          <div className="flex gap-4">
            {/* Sidebar com tabelas */}
            <Card className="w-80 shrink-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Tabelas ({DATABASE_TABLES.length})</CardTitle>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Buscar tabela..." 
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  {DATABASE_DOMAINS.map(domain => {
                    const domainTables = getTablesByDomain(domain.id).filter(t =>
                      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      t.description.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                    if (domainTables.length === 0) return null;
                    
                    return (
                      <Collapsible 
                        key={domain.id} 
                        open={expandedDomains.includes(domain.id)}
                        onOpenChange={() => toggleDomain(domain.id)}
                      >
                        <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 hover:bg-muted text-left">
                          {expandedDomains.includes(domain.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <span className={`text-sm font-medium ${domain.color}`}>{domain.name}</span>
                          <Badge variant="secondary" className="ml-auto text-xs">{domainTables.length}</Badge>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          {domainTables.map(table => (
                            <button
                              key={table.name}
                              className={`w-full text-left px-6 py-1.5 text-sm hover:bg-muted ${
                                selectedTable?.name === table.name ? 'bg-muted font-medium' : ''
                              }`}
                              onClick={() => setSelectedTable(table)}
                            >
                              {table.name}
                            </button>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Detalhes da tabela */}
            <Card className="flex-1">
              {selectedTable ? (
                <>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5" />
                      {selectedTable.name}
                    </CardTitle>
                    <CardDescription>{selectedTable.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Colunas */}
                    <div>
                      <h4 className="font-medium mb-2">Colunas ({selectedTable.columns.length})</h4>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Coluna</TableHead>
                              <TableHead>Tipo</TableHead>
                              <TableHead>Nullable</TableHead>
                              <TableHead>Default</TableHead>
                              <TableHead>Descrição</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedTable.columns.map(col => (
                              <TableRow key={col.name}>
                                <TableCell className="font-mono text-sm">{col.name}</TableCell>
                                <TableCell><Badge variant="outline">{col.type}</Badge></TableCell>
                                <TableCell>{col.nullable ? 'Sim' : 'Não'}</TableCell>
                                <TableCell className="font-mono text-xs">{col.default || '-'}</TableCell>
                                <TableCell className="text-sm text-muted-foreground">{col.description}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    {/* Foreign Keys */}
                    {selectedTable.foreignKeys.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Foreign Keys</h4>
                        <div className="space-y-1">
                          {selectedTable.foreignKeys.map((fk, i) => (
                            <div key={i} className="text-sm font-mono bg-muted p-2 rounded">
                              {fk.column} → {fk.references}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* RLS Policies */}
                    {selectedTable.rlsPolicies.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">RLS Policies</h4>
                        <div className="space-y-2">
                          {selectedTable.rlsPolicies.map((policy, i) => (
                            <div key={i} className="border rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge>{policy.command}</Badge>
                                <span className="text-sm font-medium">{policy.name}</span>
                              </div>
                              {policy.using && (
                                <div className="text-xs font-mono text-muted-foreground">
                                  USING: {policy.using.substring(0, 100)}...
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </>
              ) : (
                <CardContent className="flex items-center justify-center h-[500px] text-muted-foreground">
                  Selecione uma tabela para ver os detalhes
                </CardContent>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Hooks */}
        <TabsContent value="hooks" className="space-y-6">
          <div className="grid gap-6">
            {HOOK_CATEGORIES.map(category => {
              const hooks = getHooksByCategory(category.id);
              if (hooks.length === 0) return null;
              
              return (
                <Card key={category.id}>
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-2 ${category.color}`}>
                      {category.name}
                      <Badge variant="secondary">{hooks.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      {hooks.map(hook => (
                        <div key={hook.name} className="border rounded-lg p-4">
                          <div className="font-mono text-sm font-medium text-primary">{hook.name}</div>
                          <div className="text-sm text-muted-foreground mt-1">{hook.description}</div>
                          <div className="text-xs text-muted-foreground mt-2">{hook.path}</div>
                          {hook.returns.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {hook.returns.slice(0, 4).map(ret => (
                                <Badge key={ret.name} variant="outline" className="text-xs">
                                  {ret.name}
                                </Badge>
                              ))}
                              {hook.returns.length > 4 && (
                                <Badge variant="outline" className="text-xs">+{hook.returns.length - 4}</Badge>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Tab: Componentes */}
        <TabsContent value="componentes" className="space-y-6">
          <div className="grid gap-6">
            {COMPONENT_MODULES.map(module => (
              <Card key={module.name}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {module.name}
                    <Badge variant="secondary">{module.components.length}</Badge>
                  </CardTitle>
                  <CardDescription>{module.description}</CardDescription>
                  <code className="text-xs text-muted-foreground">{module.path}</code>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {module.components.map(comp => (
                      <Badge key={comp.name} variant="outline" className="text-xs" title={comp.description}>
                        {comp.name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab: Edge Functions */}
        <TabsContent value="edge-functions" className="space-y-6">
          {EDGE_FUNCTION_CATEGORIES.map(category => {
            const functions = getEdgeFunctionsByCategory(category.id);
            if (functions.length === 0) return null;
            
            return (
              <Card key={category.id}>
                <CardHeader>
                  <CardTitle>{category.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {functions.map(fn => (
                    <div key={fn.name} className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge>{fn.method}</Badge>
                        <code className="font-mono text-sm">{fn.endpoint}</code>
                        {fn.requiresAuth && <Badge variant="secondary">Auth</Badge>}
                        {fn.requiresAdmin && <Badge variant="destructive">Admin</Badge>}
                      </div>
                      <div className="font-medium">{fn.name}</div>
                      <div className="text-sm text-muted-foreground">{fn.description}</div>
                      {fn.body && fn.body.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs font-medium mb-1">Body:</div>
                          <div className="flex flex-wrap gap-1">
                            {fn.body.map(p => (
                              <Badge key={p.name} variant={p.required ? "default" : "outline"} className="text-xs">
                                {p.name}: {p.type}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Tab: Libs */}
        <TabsContent value="libs" className="space-y-6">
          {LIB_CATEGORIES.map(category => {
            const libs = getLibsByCategory(category.id);
            if (libs.length === 0) return null;
            
            return (
              <Card key={category.id}>
                <CardHeader>
                  <CardTitle>{category.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {libs.map(lib => (
                    <div key={lib.name} className="border rounded-lg p-4">
                      <div className="font-mono text-sm font-medium text-primary">{lib.name}</div>
                      <code className="text-xs text-muted-foreground">{lib.path}</code>
                      <div className="text-sm text-muted-foreground mt-1">{lib.description}</div>
                      <div className="mt-2 space-y-1">
                        {lib.functions.slice(0, 5).map(fn => (
                          <div key={fn.name} className="text-xs font-mono bg-muted p-1 rounded">
                            {fn.name}: <span className="text-muted-foreground">{fn.signature}</span>
                          </div>
                        ))}
                        {lib.functions.length > 5 && (
                          <div className="text-xs text-muted-foreground">+{lib.functions.length - 5} mais funções</div>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Tab: Arquitetura (original content) */}
        <TabsContent value="arquitetura" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                Arquitetura FRP Multi-Tenant
              </CardTitle>
              <CardDescription>
                Como funciona a conexão entre restaurantes locais e a nuvem
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted p-4 rounded-lg font-mono text-sm overflow-x-auto">
                <pre>{`
┌─────────────────────────────────────────────────────────────────────────┐
│                        ARQUITETURA FRP MULTI-TENANT                     │
└─────────────────────────────────────────────────────────────────────────┘

  RESTAURANTE A                    VPS (IP FIXO)                 SUPABASE
  ┌──────────────┐              ┌─────────────────┐         ┌─────────────┐
  │  Windows PC  │              │  212.85.17.133  │         │ Edge Func.  │
  │  ──────────  │              │  ─────────────  │         │ ─────────── │
  │ ACBrMonitor  │◄─── FRP ────►│   frps :7000    │◄────────│emit-nfce-   │
  │  :3434       │   Túnel      │   Porta: 7001   │  HTTPS  │  acbr       │
  │  frpc.exe    │              │                 │         │             │
  └──────────────┘              └─────────────────┘         └─────────────┘
                `}</pre>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <a
                  href="https://github.com/fatedier/frp/releases"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>FRP Releases (GitHub)</span>
                </a>
                <a
                  href="https://projeto-acbr.com.br/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>ACBr Projeto</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Para Investidores */}
        <TabsContent value="investidores" className="space-y-8">
          {/* Header Executivo */}
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 rounded-xl border">
            <h2 className="text-2xl font-bold mb-2">Altus Food - Sistema de Gestão para Restaurantes</h2>
            <p className="text-muted-foreground max-w-3xl">
              Plataforma SaaS completa que unifica PDV, estoque, fiscal, delivery e gestão financeira 
              em uma única solução moderna, com arquitetura offline-first e multi-tenant.
            </p>
          </div>

          {/* Seção 1: Visão Geral do Negócio */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Visão Geral do Negócio
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-semibold">O Problema</h4>
                  <p className="text-sm text-muted-foreground">
                    Restaurantes brasileiros operam com sistemas fragmentados: PDV de um fornecedor, 
                    controle de estoque em planilhas, fiscal em outro sistema, delivery manual. 
                    Isso gera ineficiência, erros e falta de visão integrada do negócio.
                  </p>
                </div>
                <div className="space-y-3">
                  <h4 className="font-semibold">A Solução</h4>
                  <p className="text-sm text-muted-foreground">
                    Altus Food integra todas as operações em uma única plataforma web moderna, 
                    acessível de qualquer dispositivo, funcionando mesmo offline, com preço 
                    competitivo em modelo SaaS mensal.
                  </p>
                </div>
              </div>
              
              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Mercado Alvo</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary">1M+</div>
                    <div className="text-xs text-muted-foreground">Estabelecimentos no Brasil</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary">R$ 250bi</div>
                    <div className="text-xs text-muted-foreground">Faturamento anual do setor</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary">70%</div>
                    <div className="text-xs text-muted-foreground">Sem sistema integrado</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary">SaaS</div>
                    <div className="text-xs text-muted-foreground">Modelo de negócio</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Seção 2: Stack Tecnológico */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5 text-primary" />
                Stack Tecnológico
              </CardTitle>
              <CardDescription>
                Tecnologias modernas e escaláveis utilizadas no desenvolvimento
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { 
                    layer: 'Frontend', 
                    icon: <Layers className="h-5 w-5" />,
                    techs: ['React 18.3', 'TypeScript 5.8', 'Tailwind CSS 3.4', 'Vite 5.4', 'Shadcn/UI'],
                    color: 'text-blue-500'
                  },
                  { 
                    layer: 'State Management', 
                    icon: <Database className="h-5 w-5" />,
                    techs: ['TanStack Query 5.x', 'React Context', 'Custom Hooks'],
                    color: 'text-purple-500'
                  },
                  { 
                    layer: 'Backend', 
                    icon: <Server className="h-5 w-5" />,
                    techs: ['Supabase', 'PostgreSQL 15+', 'Edge Functions (Deno)', 'RLS Multi-tenant'],
                    color: 'text-green-500'
                  },
                  { 
                    layer: 'Offline', 
                    icon: <WifiOff className="h-5 w-5" />,
                    techs: ['SQLite (sql.js)', 'IndexedDB', 'Service Worker', 'PWA'],
                    color: 'text-orange-500'
                  },
                  { 
                    layer: 'Integrações', 
                    icon: <Network className="h-5 w-5" />,
                    techs: ['QZ Tray 2.2', 'ACBr (NFC-e)', 'WhatsApp API', 'Push Notifications'],
                    color: 'text-pink-500'
                  },
                  { 
                    layer: 'Extras', 
                    icon: <Package className="h-5 w-5" />,
                    techs: ['Recharts 2.x', 'jsPDF 3.x', 'Framer Motion 12.x', 'React Hook Form'],
                    color: 'text-cyan-500'
                  },
                ].map(stack => (
                  <div key={stack.layer} className="border rounded-lg p-4">
                    <div className={`flex items-center gap-2 font-semibold mb-2 ${stack.color}`}>
                      {stack.icon}
                      {stack.layer}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {stack.techs.map(tech => (
                        <Badge key={tech} variant="secondary" className="text-xs">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Seção 3: Métricas de Desenvolvimento */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Métricas de Desenvolvimento
              </CardTitle>
              <CardDescription>
                Volume e complexidade do código desenvolvido
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: '35+', label: 'Tabelas no BD', icon: <Database className="h-6 w-6" /> },
                  { value: `${HOOKS_DOCUMENTATION.length}`, label: 'Hooks Customizados', icon: <Code className="h-6 w-6" /> },
                  { value: `${getTotalComponentsCount()}+`, label: 'Componentes React', icon: <Layers className="h-6 w-6" /> },
                  { value: `${EDGE_FUNCTIONS.length}`, label: 'Edge Functions', icon: <Cloud className="h-6 w-6" /> },
                  { value: '95', label: 'Migrations (evolução)', icon: <FileText className="h-6 w-6" /> },
                  { value: `${getTotalFunctionsCount()}`, label: 'Funções Utilitárias', icon: <Wrench className="h-6 w-6" /> },
                  { value: '50+', label: 'Páginas/Rotas', icon: <Globe className="h-6 w-6" /> },
                  { value: '800-1200h', label: 'Horas Estimadas', icon: <Clock className="h-6 w-6" /> },
                ].map(metric => (
                  <div key={metric.label} className="bg-muted rounded-lg p-4 text-center">
                    <div className="flex justify-center mb-2 text-primary">{metric.icon}</div>
                    <div className="text-2xl font-bold">{metric.value}</div>
                    <div className="text-xs text-muted-foreground">{metric.label}</div>
                  </div>
                ))}
              </div>

              <div className="bg-muted/50 border rounded-lg p-4">
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <CalendarDays className="h-5 w-5 text-primary" />
                  Timeline de Desenvolvimento
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="text-lg font-bold">23 Dez 2025</div>
                    <div className="text-xs text-muted-foreground">Início do projeto</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="text-lg font-bold">~45 dias</div>
                    <div className="text-xs text-muted-foreground">Desenvolvimento intensivo</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="text-lg font-bold">05 Fev 2026</div>
                    <div className="text-xs text-muted-foreground">Estado atual</div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  <strong>Nota:</strong> Estimativa de 800-1200 horas equivale ao tempo de programação 
                  tradicional necessário para produzir o mesmo volume e complexidade de código. 
                  O desenvolvimento foi acelerado com auxílio de IA generativa.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Seção 4: Funcionalidades Implementadas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                Funcionalidades Implementadas
              </CardTitle>
              <CardDescription>
                Todos os módulos já desenvolvidos e em produção
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { module: 'PDV (Ponto de Venda)', desc: 'Vendas por mesa, comanda, balcão e delivery', icon: <Receipt /> },
                  { module: 'Gestão de Mesas', desc: 'Mapa visual, status em tempo real, QR Code', icon: <Users /> },
                  { module: 'Comandas', desc: 'Sistema numerado com couvert e entradas', icon: <FileText /> },
                  { module: 'Cardápio Digital', desc: 'Menu público via QR Code, pedidos pelo cliente', icon: <Smartphone /> },
                  { module: 'Totem/Tablet', desc: 'Autoatendimento com modo kiosk', icon: <Smartphone /> },
                  { module: 'Controle de Caixa', desc: 'Abertura, fechamento, sangrias, suprimentos', icon: <Database /> },
                  { module: 'Período Operacional', desc: 'Controle de turnos com fechamento automático', icon: <Clock /> },
                  { module: 'Estoque de Insumos', desc: 'Ficha técnica, CMV, baixa automática', icon: <Package /> },
                  { module: 'Precificação Inteligente', desc: 'Cálculo de margens e preço sugerido', icon: <TrendingUp /> },
                  { module: 'Despesas/Financeiro', desc: 'Lançamento, categorização, DRE', icon: <Briefcase /> },
                  { module: 'Plano de Contas', desc: 'Estrutura contábil para DRE', icon: <FileText /> },
                  { module: 'Impressão Térmica', desc: 'Via QZ Tray, múltiplas impressoras', icon: <Printer /> },
                  { module: 'Fiscal (NFC-e)', desc: 'Emissão via ACBr integrado', icon: <Receipt /> },
                  { module: 'Relatórios', desc: 'Vendas, CMV, DRE, ticket médio', icon: <TrendingUp /> },
                  { module: 'App do Garçom', desc: 'PWA para lançar pedidos nas mesas', icon: <Smartphone /> },
                  { module: 'Delivery', desc: 'Painel Kanban para gestão de entregas', icon: <Rocket /> },
                  { module: 'Modo Offline', desc: 'Funciona sem internet com sync automático', icon: <WifiOff /> },
                  { module: 'Multi-tenant', desc: 'Isolamento total via RLS no PostgreSQL', icon: <Shield /> },
                ].map(feature => (
                  <div key={feature.module} className="flex items-start gap-3 p-3 border rounded-lg">
                    <div className="text-green-500 mt-0.5">{feature.icon}</div>
                    <div>
                      <div className="font-medium text-sm">{feature.module}</div>
                      <div className="text-xs text-muted-foreground">{feature.desc}</div>
                    </div>
                    <Badge variant="secondary" className="ml-auto text-xs shrink-0">Completo</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Seção 5: Arquitetura e Escalabilidade */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Arquitetura e Escalabilidade
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold flex items-center gap-2 mb-2">
                      <Shield className="h-4 w-4 text-green-500" />
                      Multi-tenancy com RLS
                    </h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Cada restaurante tem isolamento completo</li>
                      <li>• Todas as 35+ tabelas com políticas RLS</li>
                      <li>• Impossível acessar dados de outro restaurante</li>
                      <li>• Segurança em nível de banco de dados</li>
                    </ul>
                  </div>

                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold flex items-center gap-2 mb-2">
                      <Cloud className="h-4 w-4 text-blue-500" />
                      Escalabilidade Horizontal
                    </h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Backend em Supabase (auto-scaling)</li>
                      <li>• Edge Functions serverless (Deno)</li>
                      <li>• CDN para assets estáticos</li>
                      <li>• Sem servidor próprio para gerenciar</li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold flex items-center gap-2 mb-2">
                      <WifiOff className="h-4 w-4 text-orange-500" />
                      Arquitetura Offline-First
                    </h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• SQLite local para dados críticos</li>
                      <li>• IndexedDB para cache de catálogo</li>
                      <li>• Service Worker para PWA</li>
                      <li>• Sincronização automática ao reconectar</li>
                    </ul>
                  </div>

                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold flex items-center gap-2 mb-2">
                      <Zap className="h-4 w-4 text-yellow-500" />
                      Realtime
                    </h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Supabase Realtime para updates instantâneos</li>
                      <li>• Mesas mudam de status em tempo real</li>
                      <li>• Notificações push para novos pedidos</li>
                      <li>• WebSocket para comunicação bidirecional</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg font-mono text-xs overflow-x-auto">
                <pre>{`
┌──────────────────────────────────────────────────────────────────────────────────────┐
│                          ARQUITETURA MULTI-TENANT ALTUS FOOD                         │
└──────────────────────────────────────────────────────────────────────────────────────┘

                    ┌─────────────────────────────────────────────┐
                    │              SUPABASE CLOUD                  │
                    │  ┌─────────────────────────────────────────┐│
                    │  │  PostgreSQL + RLS (Row Level Security)  ││
                    │  │  Cada query filtra por restaurant_id    ││
                    │  └─────────────────────────────────────────┘│
                    │  ┌──────────┐ ┌──────────┐ ┌──────────────┐ │
                    │  │   Auth   │ │ Storage  │ │Edge Functions│ │
                    │  └──────────┘ └──────────┘ └──────────────┘ │
                    └────────────────────┬────────────────────────┘
                                         │ HTTPS / WebSocket
          ┌──────────────────────────────┼──────────────────────────────┐
          │                              │                              │
          ▼                              ▼                              ▼
    ┌──────────────┐              ┌──────────────┐              ┌──────────────┐
    │ Restaurante A│              │ Restaurante B│              │ Restaurante C│
    │   PDV + PWA  │              │   PDV + PWA  │              │   PDV + PWA  │
    │  ┌────────┐  │              │  ┌────────┐  │              │  ┌────────┐  │
    │  │ SQLite │  │              │  │ SQLite │  │              │  │ SQLite │  │
    │  │Offline │  │              │  │Offline │  │              │  │Offline │  │
    │  └────────┘  │              │  └────────┘  │              │  └────────┘  │
    └──────────────┘              └──────────────┘              └──────────────┘
                `}</pre>
              </div>
            </CardContent>
          </Card>

          {/* Seção 6: Diferenciais Competitivos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-primary" />
                Diferenciais Competitivos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    title: 'PWA Nativo',
                    desc: 'Funciona no navegador e instala como app',
                    benefit: 'Sem dependência de app stores',
                    icon: <Smartphone className="h-8 w-8" />
                  },
                  {
                    title: 'Offline Real',
                    desc: 'Continua funcionando sem internet',
                    benefit: 'Nunca para de vender',
                    icon: <WifiOff className="h-8 w-8" />
                  },
                  {
                    title: 'URL Única',
                    desc: 'Todos os clientes usam o mesmo domínio',
                    benefit: 'Simplicidade de acesso e manutenção',
                    icon: <Globe className="h-8 w-8" />
                  },
                  {
                    title: 'Impressão Térmica',
                    desc: 'Integração direta via QZ Tray',
                    benefit: 'Sem intermediários ou drivers complexos',
                    icon: <Printer className="h-8 w-8" />
                  },
                  {
                    title: 'NFC-e Integrado',
                    desc: 'Emissão fiscal nativa via ACBr',
                    benefit: 'Conformidade legal automática',
                    icon: <Receipt className="h-8 w-8" />
                  },
                  {
                    title: 'Multi-dispositivo',
                    desc: 'Celular, tablet e desktop',
                    benefit: 'Flexibilidade total de operação',
                    icon: <Layers className="h-8 w-8" />
                  },
                ].map(diff => (
                  <div key={diff.title} className="border rounded-lg p-5 text-center hover:border-primary transition-colors">
                    <div className="flex justify-center mb-3 text-primary">{diff.icon}</div>
                    <div className="font-semibold mb-1">{diff.title}</div>
                    <div className="text-sm text-muted-foreground mb-2">{diff.desc}</div>
                    <Badge variant="secondary" className="text-xs">{diff.benefit}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Seção 7: Roadmap Futuro */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-primary" />
                Roadmap Futuro
              </CardTitle>
              <CardDescription>
                Próximas funcionalidades e expansões planejadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Curto Prazo (Q1 2026)</h4>
                  <div className="space-y-2">
                    {[
                      'Engenharia de Cardápio (análise de rentabilidade)',
                      'Dashboard analytics avançado',
                      'Programa de fidelidade para clientes',
                    ].map(item => (
                      <div key={item} className="flex items-center gap-2 text-sm">
                        <div className="h-2 w-2 rounded-full bg-yellow-500" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Médio Prazo (Q2-Q3 2026)</h4>
                  <div className="space-y-2">
                    {[
                      'Integração com iFood e Rappi',
                      'App nativo para garçons (React Native)',
                      'Integração com sistemas de contabilidade',
                      'Marketplace de integrações',
                    ].map(item => (
                      <div key={item} className="flex items-center gap-2 text-sm">
                        <div className="h-2 w-2 rounded-full bg-blue-500" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="bg-muted/50 border rounded-lg p-6 text-center">
            <h3 className="font-bold text-lg mb-2">Altus Food</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Sistema completo de gestão para bares e restaurantes, desenvolvido com tecnologia de ponta 
              e arquitetura preparada para escalar.
            </p>
            <div className="flex justify-center gap-4 text-sm text-muted-foreground">
              <span>📧 contato@sejaaltus.com.br</span>
              <span>🌐 www.sejaaltus.com.br</span>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
